import {
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { FieldLabel, InfoTooltip, cn, type MessageKey } from "@dearbill/shared";
import { INFO_TRIGGER_CLASS } from "@dearbill/shared/lib/a11y";
import { INPUT_ERROR_SKIN, INPUT_SKIN } from "./fieldSkin";

type Skin = "form" | "review";

interface DateOfBirthFieldProps {
  /** The composed `YYYY-MM-DD` string, or `""` while incomplete (RHF `dob`). */
  value: string;
  onChange: (value: string) => void;
  onBlur?: (() => void) | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  autoComplete?:
    | {
        month: string;
        day: string;
        year: string;
      }
    | undefined;
  /** The active-locale translator (same one Enroll passes to every field). */
  t?: (key: MessageKey) => string;
  labels?: {
    "label.dob": string;
    "dob.month": string;
    "dob.day": string;
    "dob.year": string;
    "dob.autoAdvance": string;
    "dob.subLabel"?: string;
  };
  /**
   * `"review"` renders the M3P review step's look: `INPUT_SKIN` boxes under a
   * detail label, with no sub-label and no required marker, and the three
   * boxes grouped under the label for assistive tech. Behavior is the same in
   * both skins.
   */
  skin?: Skin | undefined;
  /** Rendered as `data-field` on the month box: the review step's failed-continue scroll anchor. */
  fieldKey?: string | undefined;
}

/** The per-skin classes around the boxes; each box's own classes are in `NumberBox`. */
const SKINS: Readonly<
  Record<Skin, { caption: string; separator: string; tooltipRow: string; error: string }>
> = {
  form: {
    caption: "text-slate-teal text-caption mt-1 text-center",
    separator: "text-cloud-blue text-[21px] leading-[60px]",
    tooltipRow: "flex h-[60px] items-center",
    error: "text-danger text-caption mt-1",
  },
  review: {
    caption: "text-hint text-slate-blue mt-1 text-center",
    separator: "text-cloud-blue text-[21px] leading-[45px]",
    tooltipRow: "flex h-[45px] items-center",
    error: "text-hint text-danger mt-1.5",
  },
};

interface Parts {
  mm: string;
  dd: string;
  yyyy: string;
}

const NON_DIGIT = /\D/g;

function splitDob(value: string): Parts {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return { mm: "", dd: "", yyyy: "" };
  return { yyyy: m[1] as string, mm: m[2] as string, dd: m[3] as string };
}

/**
 * Compose `YYYY-MM-DD` only once every part is plausibly complete (2/2/4 digits);
 * otherwise yield `""` so the schema's required / invalid-date / min-age rules
 * fire. Range validity (e.g. month 13) is left to the schema's `err.invalidDate` refine.
 */
function composeDob({ mm, dd, yyyy }: Parts): string {
  if (mm.length === 2 && dd.length === 2 && yyyy.length === 4) {
    return `${yyyy}-${mm}-${dd}`;
  }
  return "";
}

/** One numeric box and its caption. */
function NumberBox({
  id,
  boxRef,
  ariaLabel,
  caption,
  placeholder,
  autoComplete,
  value,
  maxLength,
  widthClass,
  skin,
  fieldKey,
  invalid,
  describedBy,
  onChange,
  onKeyDown,
  onBlur,
}: {
  id: string;
  boxRef: RefObject<HTMLInputElement | null>;
  ariaLabel: string;
  caption: string;
  placeholder: string;
  autoComplete: string;
  value: string;
  maxLength: number;
  widthClass: string;
  skin: Skin;
  fieldKey?: string | undefined;
  invalid: boolean;
  describedBy?: string | undefined;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (() => void) | undefined;
}): ReactNode {
  return (
    // `INPUT_SKIN` is full-width, so the review skin sizes the wrapper instead
    // of the input.
    <div className={skin === "review" ? `flex flex-col ${widthClass}` : "flex flex-col"}>
      <input
        id={id}
        ref={boxRef}
        data-field={fieldKey}
        aria-label={ariaLabel}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
        inputMode="numeric"
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        maxLength={maxLength}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        className={
          skin === "review"
            ? // A plain template string, not cn(); see the note in fieldSkin.ts.
              // `px-2!` beats the skin's own wider padding, so the placeholder
              // fits the narrow boxes.
              `${INPUT_SKIN} text-midnight px-2! text-center${invalid ? INPUT_ERROR_SKIN : ""}`
            : cn(
                "border-border bg-input-bg text-foreground h-[60px] rounded-[20px] border-2 px-2 text-center text-[15px] leading-[1.5]",
                "placeholder:text-placeholder",
                "focus:border-primary focus:bg-white focus:outline-none",
                widthClass,
                invalid && "border-danger/40 bg-danger-bg focus:border-danger",
              )
        }
      />
      <span className={SKINS[skin].caption}>{caption}</span>
    </div>
  );
}

/**
 * Date of birth entered as three boxes — MM / DD / YYYY — that compose into the
 * RHF `dob` field as `YYYY-MM-DD`. Controlled: `value` is the composed string
 * (or `""`), `onChange` receives the recomposed value on every keystroke. Typing
 * a full box auto-advances focus; Backspace on an empty box steps back.
 */
export function DateOfBirthField({
  value,
  onChange,
  onBlur,
  error,
  required,
  autoComplete = { month: "off", day: "off", year: "off" },
  t,
  labels,
  skin = "form",
  fieldKey,
}: DateOfBirthFieldProps): ReactNode {
  const mmId = useId();
  const labelId = `${mmId}-label`;
  const errorId = error ? `${mmId}-error` : undefined;
  const review = skin === "review";
  const [parts, setParts] = useState<Parts>(() => splitDob(value));

  const mmRef = useRef<HTMLInputElement>(null);
  const ddRef = useRef<HTMLInputElement>(null);
  const yyyyRef = useRef<HTMLInputElement>(null);

  // Resync the boxes when `value` changes externally (post-submit reset, dev CSV
  // import). The functional update reads the previous parts without depending on
  // them, so it only acts on external changes, not on our own onChange.
  useEffect(() => {
    setParts((prev) => (composeDob(prev) === value ? prev : splitDob(value)));
  }, [value]);

  const update = (next: Parts) => {
    setParts(next);
    onChange(composeDob(next));
  };

  const handleChange =
    (key: keyof Parts, maxLen: number, nextRef?: RefObject<HTMLInputElement | null>) =>
    (e: ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(NON_DIGIT, "").slice(0, maxLen);
      update({ ...parts, [key]: digits });
      if (digits.length === maxLen && nextRef?.current) {
        nextRef.current.focus();
      }
    };

  const handleKeyDown =
    (key: keyof Parts, prevRef?: RefObject<HTMLInputElement | null>) =>
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && parts[key] === "" && prevRef?.current) {
        // Prevent the default Backspace from landing on (and deleting the last
        // digit of) the previous box we're about to move focus into.
        e.preventDefault();
        prevRef.current.focus();
      }
    };

  const getLabelValue = (key: string) => {
    return labels?.[key as keyof typeof labels] ?? t?.(key as MessageKey) ?? "";
  };

  const invalid = Boolean(error);

  return (
    // Each box's accessible name is its own aria-label, so the review skin
    // names the group after the visible label.
    <div role={review ? "group" : undefined} aria-labelledby={review ? labelId : undefined}>
      {review ? (
        <label
          id={labelId}
          htmlFor={mmId}
          className="text-detail-label text-slate-blue block pb-1.5 uppercase"
        >
          {getLabelValue("label.dob")}
        </label>
      ) : (
        <>
          <FieldLabel htmlFor={mmId} required={required}>
            {getLabelValue("label.dob")}
          </FieldLabel>
          {getLabelValue("dob.subLabel") && (
            <p className="text-slate-teal text-caption -mt-1 mb-2">
              {getLabelValue("dob.subLabel")}
            </p>
          )}
        </>
      )}

      <div className="flex items-start gap-2">
        <NumberBox
          id={mmId}
          boxRef={mmRef}
          ariaLabel={getLabelValue("dob.month")}
          caption={getLabelValue("dob.month")}
          placeholder="MM"
          autoComplete={autoComplete.month}
          value={parts.mm}
          maxLength={2}
          widthClass="w-14"
          skin={skin}
          fieldKey={fieldKey}
          invalid={invalid}
          describedBy={errorId}
          onChange={handleChange("mm", 2, ddRef)}
          onKeyDown={handleKeyDown("mm")}
          onBlur={onBlur}
        />
        <span aria-hidden="true" className={SKINS[skin].separator}>
          /
        </span>
        <NumberBox
          id={`${mmId}-dd`}
          boxRef={ddRef}
          ariaLabel={getLabelValue("dob.day")}
          caption={getLabelValue("dob.day")}
          placeholder="DD"
          autoComplete={autoComplete.day}
          value={parts.dd}
          maxLength={2}
          widthClass="w-14"
          skin={skin}
          invalid={invalid}
          onChange={handleChange("dd", 2, yyyyRef)}
          onKeyDown={handleKeyDown("dd", mmRef)}
          onBlur={onBlur}
        />
        <span aria-hidden="true" className={SKINS[skin].separator}>
          /
        </span>
        <NumberBox
          id={`${mmId}-yyyy`}
          boxRef={yyyyRef}
          ariaLabel={getLabelValue("dob.year")}
          caption={getLabelValue("dob.year")}
          placeholder="YYYY"
          autoComplete={autoComplete.year}
          value={parts.yyyy}
          maxLength={4}
          widthClass="w-20"
          skin={skin}
          invalid={invalid}
          onChange={handleChange("yyyy", 4)}
          onKeyDown={handleKeyDown("yyyy", ddRef)}
          onBlur={onBlur}
        />
        <div className={SKINS[skin].tooltipRow}>
          <InfoTooltip
            text={getLabelValue("dob.autoAdvance")}
            label={getLabelValue("dob.autoAdvance")}
            triggerClassName={INFO_TRIGGER_CLASS}
          />
        </div>
      </div>

      {error && (
        <p id={errorId} role="alert" className={SKINS[skin].error}>
          {error}
        </p>
      )}
    </div>
  );
}
